import Component from '../../js/_Component';
import ResizeClasses from '../../js/util/_ResizeClasses';
import ScrollEnforcer from '../../js/util/_ScrollEnforcer';

export default class HeaderNavigation extends Component {
	/**
	 * The containing wrapper for the navigation menu.
	 */
	private menuWrapper: HTMLElement;

	/**
	 * The hamburger menu icon element.
	 */
	private hamburgerElement: HTMLElement;

	/**
	 * The search button element.
	 */
	private searchElement: HTMLElement;

	/**
	 * The popout overlay for the search control.
	 */
	private searchOverlay: HTMLElement;

	constructor(element: HTMLElement) {
		super(element);

		new ResizeClasses(this.element, {
			'm-mobile': {
				max: 1024,
			},
		});

		// This intentionally looks outside of the header component and influences the site--main
		// The purpose is to introduce the shadow there instead of the navigation for mobile states
		// where the nav would have had an overflow hidden.
		new ResizeClasses(document.querySelector('.site--main'), {
			'header-navigation-shadow': {
				max: 1024,
			},
		});

		this.menuWrapper = this.element.querySelector('.c-header-navigation--wrapper');
		this.hamburgerElement = this.element.querySelector('.c-header-navigation--button.m-menu');
		this.searchElement = this.element.querySelector('.c-header-navigation--button.m-search');
		this.searchOverlay = this.element.querySelector('.c-header-navigation--search-popout');

		// Allow submenus to be opened/closed.
		this.getMenuItemsWithSubmenus().forEach((item) => {
			item.addEventListener('click', () => {
				this.toggleSubmenu(item.dataset.menuIndex);
			});
		});

		// Allow the whole menu to be opened/closed on mobile.
		this.hamburgerElement.addEventListener('click', () => {
			this.toggleMenu();
		});

		// Open/close the search popout on click.
		if (this.searchElement) {
			this.searchElement.addEventListener('click', () => {
				if (this.searchOverlay.classList.contains('m-open')) {
					this.searchOverlay.classList.remove('m-open');
				} else {
					this.searchOverlay.classList.add('m-open');
					this.searchOverlay.querySelector('input').focus();
				}
			});

			// Submit search if the user presses enter within the search popout.
			this.searchOverlay.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					const query = this.searchOverlay.querySelector('input').value;
					window.open(`/search?search=${encodeURIComponent(query)}`, '_self');
				}
			});
		}

		// Force menu closed if we toggle to a different size.
		this.element.addEventListener('resizeclassesapplied', () => {
			this.closeMenu();
			this.closeAllSubmenus();
		});

		// Only update if the width changes, using the bar because it has a fixed height.
		const observer = new ResizeObserver(() => {
			this.updateSearchPopoutPosition();
			this.updateMenuHeight();
			if (this.getOpenSubmenu()) {
				this.updateSubmenuHeight(this.getOpenSubmenu());
			}
		});
		observer.observe(this.element.querySelector('.c-header-navigation--bar'));

		// Disable focusability on elements within submenus
		this.getSubmenus().forEach((submenu) => {
			HeaderNavigation.disableFocusWithin(submenu);
		});

		ScrollEnforcer.preventBoth(this.element);

		// Set binds for closing triggers
		document.addEventListener('keydown', (event) => this.handleKeydown(event));
		document.addEventListener('click', (event) => this.handleClick(event));

		this.highlightCurrentPage();

		// Remove waiting for initialization class
		setTimeout(() => {
			this.element.classList.remove('waiting');
			this.updateSearchPopoutPosition();
		}, 1000);
	}

	/**
	 * Adds the .m-current class to the menu item that matches the URL.
	 */
	highlightCurrentPage() {
		const currentLocation = window.location.pathname;

		this.getMenuItems().forEach((item) => {
			const path = item.tagName === 'A' ? item.getAttribute('href') : item.dataset.rootUrl;

			if (currentLocation.indexOf(path) === 0) {
				item.classList.add('m-current');
			}
		});
	}

	/**
	 * Disables focusability on all focusable descendants within the provided element.
	 */
	static disableFocusWithin(element: HTMLElement) {
		const focusableElements: NodeListOf<HTMLElement> = element.querySelectorAll('a, button');

		focusableElements.forEach((focusableElement) => {
			focusableElement.tabIndex = -1;
		});
	}

	/**
	 * Enables focusability on all focusable descendants within the provided element.
	 */
	static enableFocusWithin(element: HTMLElement) {
		const focusableElements: NodeListOf<HTMLElement> = element.querySelectorAll('a, button');

		focusableElements.forEach((focusableElement) => {
			focusableElement.tabIndex = 0;
		});
	}

	/**
	 * Closes the menu(s) upon clicks outside the header.
	 */
	handleClick(event) {
		// Only continue if the click target is not a descendent of the component
		if (event.target.closest('.c-header-navigation') !== null) {
			return;
		}

		if (this.getOpenSubmenu()) {
			this.closeAllSubmenus();
		}

		if (this.hasOpenMenu()) {
			this.closeMenu();
		}
	}

	/**
	 * Closes the menu(s) upon escape key presses.
	 */
	handleKeydown(event) {
		// Only continue if the escape key was pressed
		if (event.key !== 'Escape') {
			return;
		}

		if (this.getOpenSubmenu()) {
			this.closeAllSubmenus();
		}

		if (this.hasOpenMenu()) {
			this.closeMenu();
		}
	}

	/**
	 * Opens or closes the primary menu, depending on its current state.
	 */
	toggleMenu() {
		if (this.hasOpenMenu()) {
			this.closeMenu();
		} else {
			this.openMenu();
		}
	}

	/**
	 * Closes the primary menu.
	 */
	closeMenu() {
		this.element.classList.remove('m-open');
		this.hamburgerElement.classList.remove('m-active');
		this.closeAllSubmenus();

		// Call both delayed and in sequence so that the animation plays smoothly,
		// since animation from `auto` doesn't work.
		requestAnimationFrame(() => {
			this.updateMenuHeight();
		});
	}

	/**
	 * Opens the primary menu.
	 */
	openMenu() {
		this.element.classList.add('m-open');
		this.hamburgerElement.classList.add('m-active');
		this.updateMenuHeight();
	}

	/**
	 * Opens a menu by the provided menu index. If the menu is already open, it
	 * will close instead.
	 * @param index The index of the menu to open.
	 */
	toggleSubmenu(index: string) {
		if (this.getOpenSubmenu() === this.getSubmenu(index)) {
			this.closeSubmenu(index);

			if (!this.isMobile()) {
				this.closeMenu();
			}
		} else {
			this.openSubmenu(index);
		}
	}

	/**
	 * Closes a submenu.
	 */
	closeSubmenu(index: string) {
		const submenu = this.getSubmenu(index);
		const menuItem = this.getMenuItemBySubmenu(index);

		menuItem.classList.remove('m-active');
		submenu.classList.remove('m-sub-open');
		this.updateSubmenuHeight(submenu);

		HeaderNavigation.disableFocusWithin(submenu);
	}

	/**
	 * Closes all submenus except the one at the index provided.
	 * If no index is provided, all submenus are closed.
	 */
	closeAllSubmenus() {
		this.getSubmenus().forEach((submenu) => {
			this.closeSubmenu(submenu.dataset.menuIndex);
		});
	}

	/**
	 * Opens a submenu.
	 */
	openSubmenu(index: string) {
		this.closeAllSubmenus();

		const submenu = this.getSubmenu(index);
		const menuItem = this.getMenuItemBySubmenu(index);

		menuItem.classList.add('m-active');
		submenu.classList.add('m-sub-open');
		this.updateSubmenuHeight(submenu);

		this.openMenu();

		HeaderNavigation.enableFocusWithin(submenu);
	}

	private updateSearchPopoutPosition() {
		if (this.searchElement) {
			// Delay by a frame to ensure layout pass occurs.
			requestAnimationFrame(() => {
				const buttonRect = this.searchElement.getBoundingClientRect();
				const menuRect = this.element.getBoundingClientRect();
				this.searchOverlay.style.top = `${buttonRect.bottom - menuRect.top}px`;
				this.searchOverlay.style.right = `${window.innerWidth - buttonRect.right}px`;
			});
		}
	}

	/**
	 * Returns whether or not this component is in mobile mode.
	 */
	private isMobile() {
		return this.element.classList.contains('m-mobile');
	}

	/**
	 * If the menu is open, updates the menu height to be the full height of the
	 * contents. If closed, will remove the height property and fall back to CSS
	 * value.
	 */
	private updateMenuHeight() {
		if (this.hasOpenMenu() && this.isMobile()) {
			// This size comes with the expectation that the menu will generally have only one
			// height, versus changing drastically when resizing. Measuring all of its children is
			// more difficult currently, too.
			if (this.getOpenSubmenu()) {
				this.menuWrapper.style.height = 'auto';
			} else {
				this.menuWrapper.style.height = `${this.menuWrapper.scrollHeight}px`;
			}
		} else {
			if (this.menuWrapper.style.height) {
				this.menuWrapper.style.height = `${this.menuWrapper.scrollHeight}px`;
				requestAnimationFrame(() => {
					this.menuWrapper.style.removeProperty('height');
				});
			}
		}
	}

	/**
	 * Updates the height of the provided submenu based on its open state.
	 */
	private updateSubmenuHeight(submenu: HTMLElement) {
		if (submenu === this.getOpenSubmenu()) {
			// Force the menu height to the height of its contents.
			// This isn't `auto` like the menu height because it may change drastically during
			// resizing, and it is expected to have only one child element.
			submenu.style.height = `${submenu.firstElementChild.scrollHeight}px`;
		} else {
			submenu.style.removeProperty('height');
		}
	}

	/**
	 * Determines if the main menu is open or not.
	 */
	private hasOpenMenu() {
		return this.element.classList.contains('m-open');
	}

	/**
	 * Retrieves the open submenu, if one exists.
	 */
	private getOpenSubmenu() {
		return this.element.querySelector('.c-header-navigation--submenu.m-sub-open') as HTMLElement;
	}

	/**
	 * Gets all submenu elements within this navigation.
	 */
	private getSubmenus() {
		return this.element
			.querySelectorAll('.c-header-navigation--submenu') as NodeListOf<HTMLElement>;
	}

	/**
	 * Retrieves the submenu element given the provided index.
	 */
	private getSubmenu(index: string) {
		return this.element
			.querySelector(`.c-header-navigation--submenu[data-menu-index="${index}"]`) as HTMLElement;
	}

	/**
	 * Retrieves a menu item that toggles the provided submenu.
	 */
	private getMenuItemBySubmenu(index: string) {
		return this.element
			.querySelector(`.c-header-navigation--menu-item[data-menu-index="${index}"]`) as HTMLElement;
	}

	/**
	 * Retrieves all menu items that toggles a submenu.
	 */
	private getMenuItemsWithSubmenus() {
		return this.element
			.querySelectorAll('.c-header-navigation--menu-item[data-menu-index]') as NodeListOf<HTMLElement>;
	}

	/**
	 * Retrieves all menu items, both links and submenu toggles.
	 */
	private getMenuItems() {
		return this.element
			.querySelectorAll('.c-header-navigation--menu-item') as NodeListOf<HTMLElement>;
	}
}

Component.register(HeaderNavigation, 'HeaderNavigation');
